import {
  Download,
  Eye,
  MessageSquare,
  Search,
  ShoppingCart,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import { DataTablePagination } from '@/shared/components/data-table'

import { formatDisplayDate } from '../utils/purchaseOrder.utils'

import PurchaseOrderCommentsModal from './PurchaseOrderCommentsModal'

const PAGE_SIZE = 5

export function PurchaseOrderTable({
  purchaseOrders = [],
  filteredOrders = [],
  search = '',
  onSearchChange,
  isLoading = false,
  onOpenCreatePoModal,
  isAdmin: _isAdmin = false,
}) {
  const navigate = useNavigate()
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [commentOrder, setCommentOrder] = useState(null)
  const [page, setPage] = useState(1)
  const [prevSearch, setPrevSearch] = useState(search)

  const notifications = useCommentsStore((state) => state.notifications)
  const getUnreadCountForOrder = (orderId) =>
    notifications.filter(
      (n) =>
        (n.purchaseOrderId === orderId || n.purchaseOrder === orderId) &&
        !n.isRead,
    ).length

  if (prevSearch !== search) {
    setPrevSearch(search)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE))

  const currentPage = Math.min(page, totalPages)

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE

    return filteredOrders.slice(startIndex, startIndex + PAGE_SIZE)
  }, [filteredOrders, currentPage])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <section className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-xs sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="flex items-center gap-4">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#080d25] text-white">
            <ShoppingCart className="size-6" />
          </div>

          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#080d25]">
              Purchase Orders
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              View, search, and manage your purchase orders.
            </p>
          </div>
        </div>

        {Boolean(onOpenCreatePoModal) && (
          <button
            type="button"
            onClick={onOpenCreatePoModal}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#080d25] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#17203e]"
          >
            <ShoppingCart className="size-4" />
            <span>Create Purchase Order</span>
          </button>
        )}
      </section>

      {/* Purchase Orders Table Card */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xs">
        {/* Search Bar */}
        <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <label className="flex min-w-0 items-center gap-3 rounded-full bg-slate-100/80 px-4 py-3 transition focus-within:bg-slate-100 focus-within:ring-2 focus-within:ring-slate-900/10 sm:w-[min(100%,45rem)]">
            <Search className="size-5 shrink-0 text-slate-400" />

            <input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by PO number or vendor..."
              aria-label="Search purchase orders"
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
          </label>

          <span className="inline-flex w-fit items-center justify-center whitespace-nowrap rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-xs">
            Showing{' '}
            {filteredOrders.length === 0
              ? 0
              : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                  currentPage * PAGE_SIZE,
                  filteredOrders.length,
                )}`}{' '}
            of {purchaseOrders.length} purchase orders
          </span>
        </div>

        {/* Table Content */}
        {isLoading ? (
          <div className="p-16 text-center text-sm font-medium text-slate-400">
            Loading purchase orders...
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-16 text-center">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <ShoppingCart className="size-6" />
            </div>

            <p className="font-semibold text-slate-800">
              {search
                ? 'No matching purchase orders found'
                : 'No purchase orders yet'}
            </p>

            <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">
              {search
                ? 'Try searching with another PO number or vendor name.'
                : 'Purchase orders will appear here after they are created.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  {[
                    'PO Number',
                    'Date',
                    'Vendor',
                    'Payment Terms',
                    'Delivery Terms',
                    'Action',
                  ].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {paginatedOrders.map((order) => (
                  <tr
                    key={order._id}
                    className="transition hover:bg-slate-50/80"
                  >
                    <td className="whitespace-nowrap px-6 py-4.5 font-mono text-sm font-bold text-[#080d25]">
                      {order.purchaseOrderNumber}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4.5 text-sm text-slate-600">
                      {formatDisplayDate(order.purchaseOrderDate)}
                    </td>

                    <td className="px-6 py-4.5 text-sm font-medium text-slate-800">
                      {order.vendor?.vendorName || '—'}
                    </td>

                    <td className="px-6 py-4.5 text-sm text-slate-600">
                      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium">
                        {order.paymentTerms || '—'}
                      </span>
                    </td>

                    <td className="px-6 py-4.5 text-sm text-slate-600">
                      {order.deliveryTerms || '—'}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4.5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedOrder(order)}
                          aria-label={`View purchase order ${order.purchaseOrderNumber}`}
                          title="View purchase order"
                          className="inline-flex size-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-[#080d25] hover:bg-slate-100 hover:text-[#080d25]"
                        >
                          <Eye className="size-4" />
                        </button>

                        <div className="relative shrink-0">
                          <button
                            type="button"
                            onClick={() => setCommentOrder(order)}
                            aria-label={`Comments for purchase order ${order.purchaseOrderNumber}`}
                            title="Comments & Admin Communication"
                            className="inline-flex size-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-[#080d25] hover:bg-slate-100 hover:text-[#080d25]"
                          >
                            <MessageSquare className="size-4" />
                            {getUnreadCountForOrder(order._id) > 0 && (
                              <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                                {getUnreadCountForOrder(order._id) > 99
                                  ? '99+'
                                  : getUnreadCountForOrder(order._id)}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && filteredOrders.length > 0 && (
          <DataTablePagination
            page={currentPage}
            pageSize={PAGE_SIZE}
            total={filteredOrders.length}
            onPageChange={setPage}
          />
        )}
      </section>

      {/* Purchase Order Details Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
          onClick={() => setSelectedOrder(null)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-order-details-title"
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-xl sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 p-4 sm:flex-nowrap sm:px-6 sm:py-5">
              <div>
                <h2
                  id="purchase-order-details-title"
                  className="text-lg font-bold text-slate-900 sm:text-xl"
                >
                  Purchase Order Details
                </h2>

                <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                  {selectedOrder.purchaseOrderNumber || '—'}
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    navigate(
                      `/purchase/preview?id=${selectedOrder._id}&poNumber=${encodeURIComponent(
                        selectedOrder.purchaseOrderNumber || '',
                      )}`,
                    )
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#080d25] px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-[#17203e] sm:text-sm"
                >
                  <Download className="size-3.5 sm:size-4" />
                  <span>Download</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedOrder(null)}
                  aria-label="Close purchase order details"
                  className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 sm:size-9"
                >
                  <X className="size-4 sm:size-5" />
                </button>
              </div>
            </header>

            <div className="space-y-5 p-4 sm:space-y-6 sm:p-6">
              {/* General Details */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail
                  label="PO Number"
                  value={selectedOrder.purchaseOrderNumber}
                />

                <Detail
                  label="PO Date"
                  value={formatDisplayDate(selectedOrder.purchaseOrderDate)}
                />

                <Detail
                  label="Vendor"
                  value={selectedOrder.vendor?.vendorName}
                />

                <Detail
                  label="Payment Terms"
                  value={selectedOrder.paymentTerms}
                />

                <Detail
                  label="Delivery Terms"
                  value={selectedOrder.deliveryTerms}
                />
              </div>

              {/* Items */}
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  Items ({selectedOrder.items?.length ?? 0})
                </h3>

                {selectedOrder.items?.length ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full min-w-[560px]">
                      <thead className="bg-slate-50">
                        <tr>
                          {['Item', 'Quantity', 'Unit Price', 'Total'].map(
                            (heading) => (
                              <th
                                key={heading}
                                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                              >
                                {heading}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {selectedOrder.items.map((item, index) => {
                          const quantity = Number(item.quantity) || 0
                          const unitPrice = Number(item.unitPrice) || 0

                          const itemTotal =
                            item.total ??
                            item.totalPrice ??
                            quantity * unitPrice

                          return (
                            <tr key={item._id || index}>
                              <td className="px-4 py-3 text-sm text-slate-800">
                                {item.detail || '—'}
                              </td>

                              <td className="px-4 py-3 text-sm text-slate-600">
                                {item.quantity ?? '—'}
                              </td>

                              <td className="px-4 py-3 text-sm text-slate-600">
                                {item.unitPrice ?? '—'}
                              </td>

                              <td className="px-4 py-3 text-sm text-slate-600">
                                {itemTotal}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                    No items found for this purchase order.
                  </p>
                )}
              </div>
            </div>

            <footer className="flex justify-end border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="rounded-lg bg-[#080d25] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#17203e]"
              >
                Close
              </button>
            </footer>
          </section>
        </div>
      )}

      {/* Comments / Admin Messaging Modal */}
      <PurchaseOrderCommentsModal
        open={Boolean(commentOrder)}
        onOpenChange={(open) => !open && setCommentOrder(null)}
        purchaseOrder={commentOrder}
      />
    </div>
  )
}

function Detail({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="mt-1 break-words text-sm font-semibold text-slate-900">
        {value || '—'}
      </p>
    </div>
  )
}
